import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button,
    DatePicker,
    Input,
    Select,
    Switch,
} from "@jhool-io/fe-components";
import { Controller, useForm } from "react-hook-form";
import { FormatOptionLabelMeta } from "react-select";
import { useDropzone } from "react-dropzone";
import { useNavigate, useParams } from "react-router-dom";
import { parseISO } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import {
    CPT_CODES,
    NoteLabels,
    NoteTypes,
    SessionServiceType,
    ModifierTypes,
    BILLABLE_PSYCHIATRY_NOTE_TYPES,
    BILLABLE_THERAPY_NOTE_TYPES,
} from "../../../notes/types/notes.types";
import {
    cn,
    displayNameInRightFormat,
    formatDate,
    handleFormatDatePickerValue,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { useFetchDiagnosisCodes } from "../../../../hooks/queries";
import {
    CPT_CODES_DESCRIPTION,
    CPT_CODES_PSYCH,
} from "../../../../utils/constants";
import FolderOpenIcon from "../../../../components/Icons/FolderOpen";
import { useEditManualClaim } from "../../hooks/claims.mutations";
import useToast from "../../../../hooks/useToast";
import {
    AdditionalClaimInfo,
    ClaimStatus,
    ICreateManualClaim,
} from "../../types/claims.types";
import TimesIcon from "../../../../components/Icons/Times";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import { useGetClaim } from "../../hooks/claims.queries";
import { SessionPlaceOfServiceCode } from "../../../../utils/types/session";
import { useDebounce } from "../../../../hooks/helpers";

type Options = {
    label: string;
    value: string;
};

interface EditManualClaimFormProps {
    onFormSubmit: () => void;
}

interface Option {
    label: string;
    value: string;
}

const isPsychNoteValue = (noteType?: string) =>
    Boolean(
        noteType &&
            BILLABLE_PSYCHIATRY_NOTE_TYPES.includes(noteType as NoteTypes)
    );

const schema = yup.object().shape({
    pos_code: yup.string().required("POS code is required"),
    note_type: yup.string().required("Note type is required"),
    cpt_code: yup.string().when("note_type", {
        is: (value: string) => !isPsychNoteValue(value),
        then: (s) => s.required("CPT code is required"),
        otherwise: (s) => s.nullable(),
    }),
    additional_cpt_codes: yup.array().of(yup.string()).nullable(),
    duration: yup.string().when("note_type", {
        is: (value: string) => isPsychNoteValue(value),
        then: (s) =>
            s
                .required("Duration is required")
                .matches(/^[1-9]\d*$/, "Duration must be a positive number"),
        otherwise: (s) => s.nullable(),
    }),
    minutes_per_unit: yup.string().when("note_type", {
        is: (value: string) => isPsychNoteValue(value),
        then: (s) =>
            s
                .required("Minutes per unit is required")
                .matches(
                    /^[1-9]\d*$/,
                    "Minutes per unit must be a positive integer"
                ),
        otherwise: (s) => s.nullable(),
    }),
    unit_assignments: yup.array().when("note_type", {
        is: (value: string) => isPsychNoteValue(value),
        then: (s) =>
            s
                .of(
                    yup.object().shape({
                        unit: yup.string().required("Unit is required"),
                        cpt_code: yup.string().required("CPT code is required"),
                    })
                )
                .min(1, "At least one unit assignment is required"),
        otherwise: (s) => s.nullable(),
    }),
    date_of_service: yup.date().required("Date of service is required"),
    appointment_type: yup.string().required("Session type is required"),
    primary_diagnosis: yup
        .string()
        .required("Primary diagnosis code is required"),
    secondary_diagnosis: yup.string().nullable(),
    tertiary_diagnosis: yup.string().nullable(),
    additional_diagnosis_one: yup.string().nullable(),
    additional_diagnosis_two: yup.string().nullable(),
    session_start_time: yup.string().required("Session start time is required"),
    session_end_time: yup
        .string()
        .required("Session end time is required")
        .test({
            name: "is-greater",
            message: "Session end time should be later than the start time",
            test: (
                value: string | undefined,
                context: {
                    parent: {
                        session_start_time: string;
                    };
                }
            ) => {
                const { session_start_time: sessionStartTime } = context.parent;
                if (!sessionStartTime || !value) {
                    return true;
                }
                const start = new Date(`1970-01-01T${sessionStartTime}:00`);
                const end = new Date(`1970-01-01T${value}:00`);
                return end > start;
            },
        }),
});

export default function EditManualClaimForm({
    onFormSubmit,
}: EditManualClaimFormProps) {
    const { clientId, noteId } = useParams();
    const noteDetails = useFetchSessionNote(
        clientId as string,
        noteId as string,
        Boolean(clientId) && Boolean(noteId)
    );

    const [searchValue, setSearchValue] = React.useState("");
    const [activeDiagnosisField, setActiveDiagnosisField] = React.useState<
        string | null
    >(null);
    const diagnosisSearch = useDebounce(searchValue.trim(), 500);
    const [dateOfService, setDateOfService] = React.useState<Date | null>(
        parseISO(noteDetails.data?.data.date_of_service as string)
    );
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );

    const isSmallScreen = window.matchMedia("(max-width: 500px)").matches;

    const formData = new FormData();

    const {
        data: diagnosisCodes,
        isLoading,
        isFetching,
        error,
    } = useFetchDiagnosisCodes(false, diagnosisSearch);

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const editManualClaim = useEditManualClaim(
        noteDetails.data?.data.claim_id || ""
    );

    const { data: claimsData } = useGetClaim(
        {
            claim_id: noteDetails.data?.data.claim_id,
        },
        Boolean(noteDetails.data?.data.claim_id)
    );

    const primaryDiagnosis =
        noteDetails?.data?.data?.manual_note_diagnosis_codes?.find(
            (code) => code.label === "primary"
        )?.code;

    const secondaryDiagnosis =
        noteDetails?.data?.data?.manual_note_diagnosis_codes?.find(
            (code) => code.label === "secondary"
        )?.code;

    const tertiaryDiagnosis =
        noteDetails?.data?.data?.manual_note_diagnosis_codes?.find(
            (code) => code.label === "tertiary"
        )?.code;

    const additionalDiagnosisOne =
        noteDetails?.data?.data?.manual_note_diagnosis_codes?.find(
            (code) => code.label === "additional_one"
        )?.code;

    const additionalDiagnosisTwo =
        noteDetails?.data?.data?.manual_note_diagnosis_codes?.find(
            (code) => code.label === "additional_two"
        )?.code;

    const DiagnosisCodesForSelect =
        diagnosisCodes?.data.map((diagnosis) => ({
            label: `${diagnosis.code} ${diagnosis.description}`,
            value: diagnosis.diagnosis_code_id,
            code: diagnosis.code,
        })) || [];

    const existingUnitCalculation =
        noteDetails?.data?.data?.note_metadata?.unit_calculation;
    const existingAdditionalCptCodes =
        noteDetails?.data?.data?.additional_cpt_codes;
    const existingAdditionalClaimInfo =
        claimsData?.data[0].claim_content.additionalClaimInfo;

    const {
        control,
        handleSubmit,
        formState: { errors, dirtyFields },
        watch,
        setValue,
        register,
        reset,
        getValues,
    } = useForm<ICreateManualClaim>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            note_type: noteDetails?.data?.data?.type,
            primary_diagnosis: DiagnosisCodesForSelect?.find(
                (code) => code.code === primaryDiagnosis
            )?.value,

            provider_id: noteDetails?.data?.data?.provider.provider_id,
            client_id: noteDetails?.data?.data?.client.client_id,
            secondary_diagnosis: DiagnosisCodesForSelect?.find(
                (code) => code.code === secondaryDiagnosis
            )?.value,

            tertiary_diagnosis: DiagnosisCodesForSelect?.find(
                (code) => code.code === tertiaryDiagnosis
            )?.value,
            additional_one: DiagnosisCodesForSelect?.find(
                (code) => code.code === additionalDiagnosisOne
            )?.value,
            additional_two: DiagnosisCodesForSelect?.find(
                (code) => code.code === additionalDiagnosisTwo
            )?.value,

            session_start_time: formatDate(
                noteDetails?.data?.data.session_start_time || "",
                false,
                "HH:mm"
            ),
            session_end_time: formatDate(
                noteDetails?.data?.data?.session_end_time || "",
                false,
                "HH:mm"
            ),
            duration: existingUnitCalculation?.duration
                ? String(existingUnitCalculation.duration)
                : undefined,
            minutes_per_unit: existingUnitCalculation?.minutes_per_unit
                ? String(existingUnitCalculation.minutes_per_unit)
                : undefined,
            unit_assignments:
                existingUnitCalculation?.unit_assignments?.map((a) => ({
                    unit: String(a.unit),
                    cpt_code: a.cpt_code,
                })) || [],
            additional_cpt_codes: existingAdditionalCptCodes || [],
        },
    });

    const existingNoteType = noteDetails?.data?.data?.type;
    const defaultNoteCategory =
        existingNoteType && isPsychNoteValue(existingNoteType)
            ? "psych"
            : "therapy";
    const [noteCategoryOverride, setNoteCategoryOverride] = React.useState<
        "therapy" | "psych" | null
    >(null);
    const noteCategory = noteCategoryOverride ?? defaultNoteCategory;

    const isPsychNoteType = noteCategory === "psych";

    const isEditDisabled =
        claimsData?.data[0].claim_status !== ClaimStatus.READY_FOR_SUBMISSION;

    const handleChangeNoteCategory = (category: "therapy" | "psych") => {
        if (category === noteCategory) return;
        setNoteCategoryOverride(category);
        reset({
            ...getValues(),
            note_type: "" as NoteTypes,
            cpt_code: "",
            additional_cpt_codes: [],
            unit_assignments: [],
            duration: "",
            minutes_per_unit: "",
        });
    };

    const noteCategoryOptions = [
        {
            title: "Therapy",
            key: "therapy",
            disabled: isEditDisabled,
            onClick: () => handleChangeNoteCategory("therapy"),
        },
        {
            title: "Psych",
            key: "psych",
            disabled: isEditDisabled,
            onClick: () => handleChangeNoteCategory("psych"),
        },
    ];

    const durationInMinutes = watch("duration") ? Number(watch("duration")) : 0;
    const minutesPerUnit = Number(watch("minutes_per_unit"));

    const debouncedDuration = useDebounce(durationInMinutes);
    const debouncedMinutesPerUnit = useDebounce(minutesPerUnit);

    const getTotalUnitsAmount =
        debouncedMinutesPerUnit > 0
            ? debouncedDuration / debouncedMinutesPerUnit
            : 0;

    const finalUnitsAmount =
        getTotalUnitsAmount >= 0.5
            ? Math.round(getTotalUnitsAmount)
            : Math.floor(getTotalUnitsAmount);

    const watchedUnitAssignments = watch("unit_assignments");
    const currentUnitAssignments = React.useMemo(
        () => watchedUnitAssignments || [],
        [watchedUnitAssignments]
    );

    React.useEffect(() => {
        if (!isPsychNoteType || finalUnitsAmount <= 0) {
            return;
        }

        const currentLength = currentUnitAssignments.length;
        if (finalUnitsAmount === currentLength) {
            return;
        }

        if (finalUnitsAmount > currentLength) {
            const repeatCptCode =
                currentUnitAssignments[currentUnitAssignments.length - 1]
                    ?.cpt_code || "";
            const newAssignments = [...currentUnitAssignments];
            for (let i = currentLength; i < finalUnitsAmount; i += 1) {
                newAssignments.push({
                    unit: String(i + 1),
                    cpt_code: repeatCptCode,
                });
            }
            setValue("unit_assignments", newAssignments);
        } else {
            setValue(
                "unit_assignments",
                currentUnitAssignments.slice(0, finalUnitsAmount)
            );
        }
    }, [finalUnitsAmount, isPsychNoteType, currentUnitAssignments, setValue]);

    const navigate = useNavigate();

    const PlaceOfServiceOptions = [
        ...Object.values(SessionPlaceOfServiceCode).map((place) => ({
            value: place,
            label: place,
        })),
    ];

    const getSessionTypeSelectOptions = Object.values(SessionServiceType).map(
        (sessionType) => ({
            label: (
                <span style={{ textTransform: "capitalize" }}>
                    {removeEnumUnderscore(sessionType)}
                </span>
            ),
            value: sessionType,
        })
    );

    const noteTypesForCategory =
        noteCategory === "psych"
            ? BILLABLE_PSYCHIATRY_NOTE_TYPES
            : BILLABLE_THERAPY_NOTE_TYPES;

    const getNoteTypeSelectOptions = noteTypesForCategory.map((noteType) => ({
        label: NoteLabels[noteType],
        value: noteType,
    }));

    // Get filter options for diagnosis list
    const filterDiagnosisOptions = (phrases: string[]) => {
        const filteredOptions = DiagnosisCodesForSelect?.filter((option) =>
            phrases.every(
                (phrase) =>
                    option &&
                    option.label.toLowerCase().includes(phrase.toLowerCase())
            )
        );
        return filteredOptions;
    };

    const modifiersTypesForSelect: Options[] = Object.values(ModifierTypes).map(
        (item) => ({
            value: item,
            label: item,
        })
    );

    const additionalClaimInfoForSelect: Options[] = Object.values(
        AdditionalClaimInfo
    ).map((info) => ({
        value: info,
        label: info,
    }));

    const CPTOptions = CPT_CODES.sort((a, b) => Number(a) - Number(b)).map(
        (code) => ({
            label: `${removeEnumUnderscore(code)} - ${
                CPT_CODES_DESCRIPTION[code]
            }`,
            value: code,
        })
    );

    const PsychCPTOptions = CPT_CODES_PSYCH.map((code) => ({
        label: `${removeEnumUnderscore(code)} - ${CPT_CODES_DESCRIPTION[code]}`,
        value: code,
    }));

    // Function to call when file(s) are dropped in dropzone
    const onDrop = React.useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else setSelectedFiles([...acceptedFiles]);
        },
        [selectedFiles]
    );

    // Initialize the useDropzone hook
    const { getRootProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: {
            "application/pdf": [".pdf"],
            "image/*": [".png", ".webp", ".jpeg", ".jpg", ".avif"],
        },
    });

    // Function for setting selected files
    const handleSetSelectedFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...e.target.files, ...selectedFiles]);
            } else setSelectedFiles([...e.target.files]);
        }
    };

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const convertZeroTimeFromDoubleToSingle = (timeString: string) => {
        return timeString === "00" ? "0" : timeString;
    };

    const onSubmit = (payload: ICreateManualClaim) => {
        let diagnosisToSend: { id: string; label: string }[] = [
            {
                id: dirtyFields.primary_diagnosis
                    ? payload.primary_diagnosis || ""
                    : DiagnosisCodesForSelect?.find((code) =>
                          payload?.primary_diagnosis?.startsWith(code.value)
                      )?.value || "",
                label: "primary",
            },
        ];

        if (payload.secondary_diagnosis) {
            diagnosisToSend = [
                ...diagnosisToSend,
                {
                    label: "secondary",
                    id: dirtyFields.secondary_diagnosis
                        ? payload.secondary_diagnosis || ""
                        : DiagnosisCodesForSelect?.find((code) =>
                              payload?.secondary_diagnosis?.startsWith(
                                  code.value
                              )
                          )?.value || "",
                },
            ];
        }

        if (payload.tertiary_diagnosis) {
            diagnosisToSend = [
                ...diagnosisToSend,
                {
                    label: "tertiary",
                    id: dirtyFields.tertiary_diagnosis
                        ? payload.tertiary_diagnosis || ""
                        : DiagnosisCodesForSelect?.find((code) =>
                              payload?.tertiary_diagnosis?.startsWith(
                                  code.value
                              )
                          )?.value || "",
                },
            ];
        }

        if (payload.additional_one) {
            diagnosisToSend = [
                ...diagnosisToSend,
                {
                    label: "additional_one",
                    id: dirtyFields.additional_one
                        ? payload.additional_one || ""
                        : DiagnosisCodesForSelect?.find((code) =>
                              payload?.additional_one?.startsWith(code.value)
                          )?.value || "",
                },
            ];
        }

        if (payload.additional_two) {
            diagnosisToSend = [
                ...diagnosisToSend,
                {
                    label: "additional_two",
                    id: dirtyFields.additional_two
                        ? payload.additional_two || ""
                        : DiagnosisCodesForSelect?.find((code) =>
                              payload?.additional_two?.startsWith(code.value)
                          )?.value || "",
                },
            ];
        }

        const [sessionStartHr, sesssionStartMin] =
            payload.session_start_time.split(":");
        const [sessionEndHr, sesssionEndMin] =
            payload.session_end_time.split(":");

        formData.append("provider_id", payload.provider_id);
        formData.append("client_id", payload.client_id);
        formData.append(
            "session_start_hr",
            convertZeroTimeFromDoubleToSingle(sessionStartHr)
        );
        formData.append(
            "session_start_min",
            convertZeroTimeFromDoubleToSingle(sesssionStartMin)
        );
        formData.append(
            "session_end_hr",
            convertZeroTimeFromDoubleToSingle(sessionEndHr)
        );
        formData.append(
            "session_end_min",
            convertZeroTimeFromDoubleToSingle(sesssionEndMin)
        );
        formData.append("note_type", payload.note_type);
        formData.append("appointment_type", String(payload.appointment_type));
        formData.append("pos_code", payload.pos_code);
        formData.append(
            "date_of_service",
            handleFormatDatePickerValue(payload.date_of_service)
        );

        if (isPsychNoteType) {
            const assignments = payload.unit_assignments || [];
            const primaryCptCode = assignments[0]?.cpt_code || "";
            formData.append("cpt_code", primaryCptCode);

            const uniqueAdditional = Array.from(
                new Set(
                    assignments
                        .map((a) => a.cpt_code)
                        .filter((code, index) => code && index > 0)
                )
            ).filter((code) => code !== primaryCptCode);
            formData.append(
                "additional_cpt_codes",
                JSON.stringify(uniqueAdditional)
            );

            formData.append(
                "unit_calculation",
                JSON.stringify({
                    duration: Number(payload.duration) || 0,
                    total_units: finalUnitsAmount || 0,
                    minutes_per_unit: Number(payload.minutes_per_unit) || 0,
                    unit_assignments: assignments.map((a) => ({
                        unit: Number(a.unit),
                        cpt_code: a.cpt_code,
                    })),
                })
            );
        } else {
            formData.append("cpt_code", payload.cpt_code);
            const additional = (payload.additional_cpt_codes || []).filter(
                (code) => code && code !== payload.cpt_code
            );
            formData.append("additional_cpt_codes", JSON.stringify(additional));
        }

        // cleanse diagnosis for ids which are empty
        const filteredDiagnosisCodes = diagnosisToSend?.filter(
            (item) => item.id !== ""
        );

        if (filteredDiagnosisCodes.length > 0) {
            for (let i = 0; i < filteredDiagnosisCodes.length; i += 1) {
                formData.append(
                    "diagnosis_codes",
                    JSON.stringify(filteredDiagnosisCodes)
                );
            }
        }

        if (payload.modifier_codes && payload.modifier_codes.length > 0) {
            for (let i = 0; i < [...payload.modifier_codes].length; i += 1) {
                formData.append(
                    "modifier_codes",
                    JSON.stringify(payload.modifier_codes)
                );
            }
        }

        const additionalClaimInfo = dirtyFields.additional_claim_info
            ? payload.additional_claim_info
            : existingAdditionalClaimInfo;
        formData.append("additional_claim_info", additionalClaimInfo || "");

        if (selectedFiles && selectedFiles.length > 0) {
            for (let i = 0; i < [...selectedFiles].length; i += 1) {
                formData.append("note_file", [...selectedFiles][i]);
            }
        }
        editManualClaim.mutate(formData, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });
                toast({
                    mode: "success",
                    message:
                        res.message || "Manual claim updated successfully!",
                });
                onFormSubmit();
                navigate(
                    `/claims/${res.claim_id}/${res.data.claim.client_id}/${res.data.claim.note_id}`
                );
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not create claim at this time.",
                });
            },
        });
    };

    return (
        <form id="edit-manual-claim" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Input
                    label="Provider name"
                    value={`${displayNameInRightFormat({
                        firstName:
                            noteDetails?.data?.data.provider.first_name || "",
                        lastName:
                            noteDetails?.data?.data.provider.last_name || "",
                    })}`}
                    disabled
                />
            </div>
            <div className="fg">
                <Input
                    label="Client name"
                    value={`${displayNameInRightFormat({
                        firstName:
                            noteDetails?.data?.data.client.first_name || "",
                        lastName:
                            noteDetails?.data?.data.client.last_name || "",
                    })}`}
                    disabled
                />
            </div>
            <div className="fg">
                <Controller
                    name="date_of_service"
                    control={control}
                    defaultValue={noteDetails.data?.data.date_of_service}
                    render={({ field }) => (
                        <DatePicker
                            label="Date of Service"
                            selected={dateOfService}
                            onChange={(date) => {
                                field.onChange(date);
                                setDateOfService(date);
                            }}
                            hasError={!!errors.date_of_service}
                            errorText={
                                errors.date_of_service?.type === "typeError"
                                    ? "invalid date value"
                                    : errors.date_of_service?.message
                            }
                            maxDate={new Date(Date.now())}
                            disabled={
                                claimsData?.data[0].claim_status !==
                                ClaimStatus.READY_FOR_SUBMISSION
                            }
                        />
                    )}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    {...register("session_start_time")}
                    label="Start time"
                    placeholder="Start time"
                    hasError={!!errors.session_start_time}
                    errorText={errors.session_start_time?.message}
                    type="time"
                />

                <Input
                    {...register("session_end_time")}
                    label="End time"
                    placeholder="End time"
                    hasError={!!errors.session_end_time}
                    errorText={errors.session_end_time?.message}
                    type="time"
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="appointment_type"
                    control={control}
                    defaultValue={
                        getSessionTypeSelectOptions.find(
                            (option) =>
                                option.value ===
                                noteDetails.data?.data.appointment_type
                        )?.value
                    }
                    render={({ field }) => (
                        <Select
                            label="Session type"
                            placeholder="Select session type"
                            isSearchable
                            options={getSessionTypeSelectOptions}
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Options).value);
                            }}
                            value={getSessionTypeSelectOptions.find(
                                (option) =>
                                    option.value === watch("appointment_type")
                            )}
                            defaultValue={getSessionTypeSelectOptions.find(
                                (option) =>
                                    option.value ===
                                    noteDetails.data?.data.appointment_type
                            )}
                            hasError={!!errors.appointment_type}
                            errorText={errors.appointment_type?.message}
                            isDisabled={
                                claimsData?.data[0].claim_status !==
                                ClaimStatus.READY_FOR_SUBMISSION
                            }
                        />
                    )}
                />
                <Controller
                    name="pos_code"
                    control={control}
                    defaultValue={
                        PlaceOfServiceOptions.find(
                            (pos) =>
                                pos.value === noteDetails.data?.data.pos_code
                        )?.value
                    }
                    render={({ field }) => (
                        <Select
                            label="POS code"
                            placeholder="Select POS code"
                            options={PlaceOfServiceOptions}
                            isSearchable
                            isLongListInDialog
                            onChange={(val) =>
                                field.onChange((val as Options).value)
                            }
                            defaultValue={PlaceOfServiceOptions.find(
                                (pos) =>
                                    pos.value ===
                                    noteDetails.data?.data.pos_code
                            )}
                            hasError={!!errors.pos_code}
                            errorText={errors.pos_code?.message}
                            formatOptionLabel={(
                                selectData: unknown,
                                formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                            ) => {
                                return formatOptionLabelMeta.context === "menu"
                                    ? (selectData as Options).label
                                    : truncateString(
                                          (selectData as Options)
                                              .label as string,
                                          35
                                      );
                            }}
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Switch
                    switchOptions={noteCategoryOptions}
                    activeSwitch={noteCategory}
                />
            </div>
            <div className="fg">
                <Controller
                    name="note_type"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Note type"
                            placeholder="Select note type"
                            isSearchable
                            options={getNoteTypeSelectOptions}
                            isLongListInDialog
                            value={
                                getNoteTypeSelectOptions.find(
                                    (type) => type.value === field.value
                                ) || null
                            }
                            onChange={(val) => {
                                field.onChange((val as Options).value);
                            }}
                            hasError={!!errors.note_type}
                            errorText={errors.note_type?.message}
                            isDisabled={isEditDisabled}
                        />
                    )}
                />
            </div>
            {isPsychNoteType ? (
                <>
                    <div className="fg fg-space-between three flex">
                        <Input
                            {...register("duration")}
                            label="Duration (in minutes)"
                            placeholder="Duration"
                            hasError={!!errors.duration}
                            errorText={errors.duration?.message}
                            disabled={
                                claimsData?.data[0].claim_status !==
                                ClaimStatus.READY_FOR_SUBMISSION
                            }
                        />
                        <Input
                            {...register("minutes_per_unit")}
                            label="Mins per unit"
                            placeholder="Mins per unit"
                            hasError={!!errors.minutes_per_unit}
                            errorText={errors.minutes_per_unit?.message}
                            disabled={
                                claimsData?.data[0].claim_status !==
                                ClaimStatus.READY_FOR_SUBMISSION
                            }
                        />
                        <Input
                            label="Total units"
                            placeholder="Total units"
                            value={finalUnitsAmount}
                            disabled
                        />
                    </div>
                    {currentUnitAssignments.map((assignment, i) => (
                        <div
                            key={`unit-${i + 1}`}
                            className="fg flex justify-between gap-[--spacing-items]"
                        >
                            <div className="basis-[80px]">
                                <Input
                                    {...register(`unit_assignments.${i}.unit`)}
                                    label="Unit"
                                    placeholder="Unit"
                                    defaultValue={assignment.unit}
                                    disabled
                                />
                            </div>
                            <div className="flex-1">
                                <Controller
                                    name={`unit_assignments.${i}.cpt_code`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="CPT Code"
                                            placeholder="CPT Code"
                                            options={PsychCPTOptions}
                                            value={PsychCPTOptions.find(
                                                (code) =>
                                                    code.value === field.value
                                            )}
                                            onChange={(val) =>
                                                field.onChange(
                                                    (val as Options).value
                                                )
                                            }
                                            formatOptionLabel={(
                                                selectData,
                                                meta
                                            ) => {
                                                return meta.context === "menu"
                                                    ? (selectData as Options)
                                                          .label
                                                    : truncateString(
                                                          (
                                                              selectData as Options
                                                          ).label,
                                                          isSmallScreen
                                                              ? 20
                                                              : 60
                                                      );
                                            }}
                                            isSearchable
                                            isLongListInDialog
                                            hasError={
                                                !!errors.unit_assignments?.[i]
                                                    ?.cpt_code
                                            }
                                            errorText={
                                                errors.unit_assignments?.[i]
                                                    ?.cpt_code?.message
                                            }
                                            isDisabled={
                                                claimsData?.data[0]
                                                    .claim_status !==
                                                ClaimStatus.READY_FOR_SUBMISSION
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    ))}
                </>
            ) : (
                <>
                    <div className="fg">
                        <Controller
                            name="cpt_code"
                            control={control}
                            defaultValue={
                                CPTOptions.find(
                                    (code) =>
                                        code.value ===
                                        noteDetails.data?.data.cpt_code
                                )?.value
                            }
                            render={({ field }) => (
                                <Select
                                    label="CPT Code"
                                    placeholder="CPT Code"
                                    options={CPTOptions}
                                    onChange={(val) =>
                                        field.onChange((val as Options).value)
                                    }
                                    hasError={!!errors.cpt_code}
                                    errorText={errors.cpt_code?.message}
                                    isSearchable
                                    isLongListInDialog
                                    defaultValue={CPTOptions.find(
                                        (code) =>
                                            code.value ===
                                            noteDetails.data?.data.cpt_code
                                    )}
                                    formatOptionLabel={(
                                        selectData: unknown,
                                        formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                                    ) => {
                                        return formatOptionLabelMeta.context ===
                                            "menu"
                                            ? (selectData as Options).label
                                            : truncateString(
                                                  (selectData as Options)
                                                      .label as string,
                                                  35
                                              );
                                    }}
                                    isDisabled={
                                        claimsData?.data[0].claim_status !==
                                        ClaimStatus.READY_FOR_SUBMISSION
                                    }
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="additional_cpt_codes"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label="Additional CPT codes"
                                    placeholder="Additional CPT codes"
                                    options={CPTOptions}
                                    isMulti
                                    isSearchable
                                    isLongListInDialog
                                    value={CPTOptions.filter((option) =>
                                        (field.value || []).includes(
                                            option.value
                                        )
                                    )}
                                    onChange={(val) => {
                                        field.onChange(
                                            (
                                                val as {
                                                    label: string;
                                                    value: string;
                                                }[]
                                            ).map((code) => code.value)
                                        );
                                    }}
                                    multiHasValues={
                                        (watch("additional_cpt_codes") || [])
                                            .length > 0
                                    }
                                    isDisabled={
                                        claimsData?.data[0].claim_status !==
                                        ClaimStatus.READY_FOR_SUBMISSION
                                    }
                                />
                            )}
                        />
                    </div>
                </>
            )}
            <div className="fg">
                <Controller
                    name="primary_diagnosis"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Primary diagnosis"
                            placeholder="Primary diagnosis"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          searchValue.split(" ")
                                      )
                            }
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 40
                                      );
                            }}
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setSearchValue("");
                                }
                            }}
                            isSearchable
                            isLongListInDialog
                            filterOption={() => true}
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            defaultValue={
                                DiagnosisCodesForSelect?.find((code) =>
                                    code.value?.startsWith(field.value)
                                ) || null
                            }
                            hasError={!!errors.primary_diagnosis}
                            errorText={errors.primary_diagnosis?.message}
                            closeMenuOnSelect
                        />
                    )}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="secondary_diagnosis"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Secondary diagnosis"
                            placeholder="Secondary diagnosis"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          searchValue.split(" ")
                                      )
                            }
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setSearchValue("");
                                }
                            }}
                            isSearchable
                            isLongListInDialog
                            filterOption={() => true}
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Option).value);
                            }}
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 25
                                      );
                            }}
                            defaultValue={
                                DiagnosisCodesForSelect?.find((code) =>
                                    field.value?.startsWith(code.value)
                                ) || null
                            }
                            hasError={!!errors.secondary_diagnosis}
                            errorText={errors.secondary_diagnosis?.message}
                            closeMenuOnSelect
                            isClearable
                        />
                    )}
                />
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label="Tertiary diagnosis"
                                placeholder="Tertiary diagnosis"
                                options={
                                    diagnosisSearch.length >= 2
                                        ? DiagnosisCodesForSelect
                                        : filterDiagnosisOptions(
                                              searchValue.split(" ")
                                          )
                                }
                                isLoading={
                                    isFetching &&
                                    activeDiagnosisField === field.name
                                }
                                isDisabled={
                                    (isLoading || Boolean(error)) &&
                                    diagnosisSearch.length < 2
                                }
                                onInputChange={(value, { action }) => {
                                    if (action === "input-change") {
                                        setActiveDiagnosisField(field.name);
                                        setSearchValue(value);
                                    }
                                    if (
                                        action === "set-value" ||
                                        action === "menu-close"
                                    ) {
                                        setActiveDiagnosisField(null);
                                        setSearchValue("");
                                    }
                                }}
                                isSearchable
                                isLongListInDialog
                                filterOption={() => true}
                                onChange={(val) => {
                                    if (val === null) {
                                        field.onChange(null);
                                        return;
                                    }
                                    field.onChange((val as Option).value);
                                }}
                                defaultValue={
                                    DiagnosisCodesForSelect?.find((code) =>
                                        field.value?.startsWith(code.value)
                                    ) || null
                                }
                                formatOptionLabel={(selectData, meta) => {
                                    return meta.context === "menu"
                                        ? (selectData as Option).label
                                        : truncateString(
                                              (selectData as Option).label,
                                              isSmallScreen ? 20 : 25
                                          );
                                }}
                                hasError={!!errors.tertiary_diagnosis}
                                errorText={errors.tertiary_diagnosis?.message}
                                closeMenuOnSelect
                                isClearable
                            />
                        )}
                    />
                </div>
            </div>
            <div className="fg fg-space-between two flex">
                <Controller
                    name="additional_one"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Additional diagnosis 1"
                            placeholder="Additional diagnosis 1"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          searchValue.split(" ")
                                      )
                            }
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setSearchValue("");
                                }
                            }}
                            isSearchable
                            isLongListInDialog
                            filterOption={() => true}
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Option).value);
                            }}
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 25
                                      );
                            }}
                            defaultValue={
                                DiagnosisCodesForSelect?.find((code) =>
                                    field.value?.startsWith(code.value)
                                ) || null
                            }
                            hasError={!!errors.additional_one}
                            errorText={errors.additional_one?.message}
                            closeMenuOnSelect
                            isClearable
                        />
                    )}
                />

                <Controller
                    name="additional_two"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Additional diagnosis 2"
                            placeholder="Additional diagnosis 2"
                            options={
                                diagnosisSearch.length >= 2
                                    ? DiagnosisCodesForSelect
                                    : filterDiagnosisOptions(
                                          searchValue.split(" ")
                                      )
                            }
                            isLoading={
                                isFetching &&
                                activeDiagnosisField === field.name
                            }
                            isDisabled={
                                (isLoading || Boolean(error)) &&
                                diagnosisSearch.length < 2
                            }
                            onInputChange={(value, { action }) => {
                                if (action === "input-change") {
                                    setActiveDiagnosisField(field.name);
                                    setSearchValue(value);
                                }
                                if (
                                    action === "set-value" ||
                                    action === "menu-close"
                                ) {
                                    setActiveDiagnosisField(null);
                                    setSearchValue("");
                                }
                            }}
                            isSearchable
                            isLongListInDialog
                            filterOption={() => true}
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Option).value);
                            }}
                            formatOptionLabel={(selectData, meta) => {
                                return meta.context === "menu"
                                    ? (selectData as Option).label
                                    : truncateString(
                                          (selectData as Option).label,
                                          isSmallScreen ? 20 : 25
                                      );
                            }}
                            defaultValue={
                                DiagnosisCodesForSelect?.find((code) =>
                                    field.value?.startsWith(code.value)
                                ) || null
                            }
                            hasError={!!errors.additional_two}
                            errorText={errors.additional_two?.message}
                            closeMenuOnSelect
                            isClearable
                        />
                    )}
                />
            </div>

            <div className="fg">
                <Controller
                    name="modifier_codes"
                    control={control}
                    defaultValue={noteDetails.data?.data.modifier_codes}
                    render={({ field }) => (
                        <Select
                            label="Modifier code"
                            placeholder="Modifier code"
                            options={modifiersTypesForSelect}
                            isSearchable
                            isLongListInDialog
                            isMulti
                            defaultValue={modifiersTypesForSelect?.filter(
                                (code) =>
                                    noteDetails?.data?.data.modifier_codes?.includes(
                                        code.value.split(" ")[0]
                                    )
                            )}
                            onChange={(val) => {
                                field.onChange(
                                    (
                                        val as {
                                            label: string;
                                            value: string;
                                        }[]
                                    ).map((code) => code.value.split(" ")[0])
                                );
                            }}
                            multiHasValues={
                                Boolean(watch("modifier_codes")) ||
                                Boolean(
                                    noteDetails.data?.data.modifier_codes &&
                                        noteDetails.data?.data.modifier_codes
                                            .length > 0
                                )
                            }
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="additional_claim_info"
                    control={control}
                    defaultValue={existingAdditionalClaimInfo}
                    render={({ field }) => (
                        <Select
                            label="Additional claim info (Box 19)"
                            placeholder="Additional claim info (Box 19)"
                            options={additionalClaimInfoForSelect}
                            isLongListInDialog
                            defaultValue={
                                additionalClaimInfoForSelect.find(
                                    (info) =>
                                        info.value ===
                                        existingAdditionalClaimInfo
                                ) || null
                            }
                            onChange={(val) => {
                                if (val === null) {
                                    field.onChange(null);
                                    return;
                                }
                                field.onChange((val as Options).value);
                            }}
                            isClearable
                        />
                    )}
                />
            </div>
            {claimsData?.data[0].claim_status ===
                ClaimStatus.READY_FOR_SUBMISSION && (
                <>
                    {" "}
                    <div
                        className={cn(
                            "flex w-full items-center gap-x-[13px] pt-[31px] pr-[92px] pb-[30px] pl-24 border border-dashed border-primary rounded-[16px]",
                            {
                                "opacity-20": selectedFiles?.length === 1,
                                "border-[2px] border-dashed border-primary-dark":
                                    isDragActive,
                            }
                        )}
                        {...getRootProps()}
                    >
                        <div className="px-[17px] py-16 border border-[#D3F7FA]  bg-primary-50 rounded-[9.667px]">
                            <FolderOpenIcon />
                        </div>
                        <div className="flex flex-col">
                            <div className="text-sm font-medium">
                                <label
                                    htmlFor="file-upload"
                                    className="font-semibold text-primary"
                                >
                                    Click to upload session note{" "}
                                    <input
                                        className="w-full absolute left-[-99999px]"
                                        id="file-upload"
                                        name="file-upload"
                                        type="file"
                                        onChange={handleSetSelectedFiles}
                                        disabled={selectedFiles?.length === 1}
                                        max="1"
                                    />
                                </label>
                                or drag your file here
                            </div>
                            <p className="text-xs font-normal">
                                Maximum file size of 10 MB: .png, .jpg, .jpeg
                            </p>
                        </div>
                    </div>
                    {editManualClaim.uploadProgress > 0 && (
                        <div className="mt-5 h-4 bg-[rgba(185_186_163_0.4)]">
                            <div
                                role="progressbar"
                                aria-label="file upload progress value"
                                aria-valuenow={editManualClaim.uploadProgress}
                                style={{
                                    width: `${editManualClaim.uploadProgress}%`,
                                }}
                                className="h-full rounded-r8 bg-primary"
                            />
                        </div>
                    )}
                    <div className="mt-16">
                        {selectedFiles &&
                            selectedFiles.map((file, i) => (
                                <div
                                    key={`${file.name} - ${i + 1}`}
                                    className="flex items-center justify-between w-full gap-x-16 mt-24"
                                >
                                    <div className=" border border-strokedark rounded-r6 w-full px-10 py-[6px] font-normal text-xs ">
                                        {file.name}
                                    </div>
                                    <Button
                                        variant="normal"
                                        aria-label="delete icon"
                                        className="flex bg-card-bg !rounded-full p-8"
                                        onClick={() =>
                                            handleRemoveSelectedFile(file.name)
                                        }
                                    >
                                        <TimesIcon stroke="#8E8E8E" />
                                    </Button>
                                </div>
                            ))}
                    </div>
                </>
            )}
        </form>
    );
}
